import { Box, Flex, Text, theme, useBoolean } from '@chakra-ui/react';
import { ReactComponent as RandomIcon } from 'assets/icons/random.svg';
import { StepColorPickerContainer } from 'modules/Editor/components/ColorPicker';
import { PermissionBadge } from 'modules/User';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { SIDE_MENU_WIDTH } from '../../WidgetMenu.config';

interface ColorPaletteProps {
  first: boolean;
  last: boolean;
  color: string;
  count: number;
}

export const ColorRect = memo(({ first, last, color, count }: Readonly<ColorPaletteProps>) => {
  return (
    <Box
      background={color}
      w={`${(SIDE_MENU_WIDTH - 32) / count}px`}
      h='34px'
      borderTopLeftRadius={first ? 'base' : 0}
      borderBottomLeftRadius={first ? 'base' : 0}
      borderTopRightRadius={last ? 'base' : 0}
      borderBottomRightRadius={last ? 'base' : 0}
    ></Box>
  );
});

ColorRect.displayName = 'ColorRect';

interface ColorPaletteSetProps {
  palette: string[];
  showBadge?: boolean;
  onShuffle: (colors: string[]) => void;
}

const ColorPaletteSet = memo(({ palette, showBadge, onShuffle }: ColorPaletteSetProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.stylesMenu' });
  const [isHovered, setIsHovered] = useBoolean(false);

  const handleClickPalette = useCallback(() => onShuffle(palette), [palette, onShuffle]);

  return (
    <StepColorPickerContainer
      display={'flex'}
      flexFlow={'row'}
      mx='auto'
      my='0.5rem'
      cursor={'pointer'}
      position='relative'
      onClick={handleClickPalette}
      onMouseEnter={setIsHovered.on}
      onMouseLeave={setIsHovered.off}
      onFocus={setIsHovered.on}
      onBlur={setIsHovered.off}
      as='button'
      aria-label={t('shuffleColor')}
      data-testid='color-palette-set'
      role='group'
    >
      {showBadge && <PermissionBadge permission={'business'} position={'absolute'} top={-2} right={2} />}
      <Box
        position='absolute'
        color='#FFF'
        w='100%'
        display={showBadge ? 'none' : 'flex'}
        justifyContent='center'
        placeItems={'center'}
        pt='5px'
        pb='6px'
        borderRadius={'base'}
        background={isHovered ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)'}
        transition='background .33s'
      >
        <Text
          fontWeight={'medium'}
          color={isHovered ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0)'}
          transition='color .33s'
        >
          {t('shuffle')}
        </Text>
        <RandomIcon
          color={isHovered ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0)'}
          style={{ transition: 'color .33s', marginLeft: theme.space[2] }}
        />
      </Box>
      {palette.map((c, subIndex) => {
        const isFirst = subIndex === 0;
        const isLast = subIndex === palette.length - 1;
        return <ColorRect first={isFirst} last={isLast} color={c} key={`color_${subIndex}`} count={palette.length} />;
      })}
    </StepColorPickerContainer>
  );
});

ColorPaletteSet.displayName = 'ColorPaletteSet';

interface ColorPaletteListProps {
  colorPaletteList: Array<string[]>;
  hasBrandKitPermission?: boolean;
  onShuffle: (colors: string[]) => void;
}

export const ColorPaletteList = memo(
  ({ colorPaletteList, hasBrandKitPermission, onShuffle }: ColorPaletteListProps) => {
    return (
      <Flex flexFlow={'column wrap'} marginInlineStart='unset'>
        {colorPaletteList.map((palette, index) => (
          <ColorPaletteSet
            key={`color_palettes_${index}`}
            palette={palette}
            showBadge={!hasBrandKitPermission}
            onShuffle={onShuffle}
          />
        ))}
      </Flex>
    );
  },
);

ColorPaletteList.displayName = 'ColorPaletteList';
